import React, { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { useTheme } from '@mui/material/styles';
import { Popover } from '@mui/material';
import NRNotificationsInAppContainer from './NRNotificationsInAppContainer';
import useLocalStorageService from '../../services/local';

const STORAGE_KEY = 'ReadNotifications';

function NRNotificationsInApp() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [readNotifications, setReadNotifications] = useState(new Set());
  const [notifications, setNotifications] = useState([]);
  const theme = useTheme();
  const { getStorageKeyData, setStorageKeyData } = useLocalStorageService();

  // Load read notifications from localStorage on mount
  useEffect(() => {
    const storedData = getStorageKeyData(STORAGE_KEY);
    if (storedData?.readNotificationIds) {
      setReadNotifications(new Set(storedData.readNotificationIds));
    }
  }, [getStorageKeyData]);

  // Check if there are unread notifications
  const hasUnreadNotifications = React.useMemo(
    () => notifications.some(notification => !readNotifications.has(notification.id)),
    [notifications, readNotifications]
  );

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = notificationId => {
    // Mark notification as read
    setReadNotifications(prev => {
      const updated = new Set(prev).add(notificationId);

      // Persist to localStorage using the service
      const readIds = Array.from(updated);
      setStorageKeyData(STORAGE_KEY, {
        key: 'ReadNotificationIds',
        value: readIds
      });

      return updated;
    });

    // In real implementation, you would also:
    // - Send to backend: markNotificationAsRead(notificationId);
    // - Or update in your state management (Redux, Context, etc.)
  };

  const handleMarkAllAsRead = () => {
    if (notifications.length === 0) return;

    // Mark all notifications as read
    const allNotificationIds = notifications.map(n => n.id);
    setReadNotifications(prev => {
      const updated = new Set([...prev, ...allNotificationIds]);

      // Persist to localStorage
      const readIds = Array.from(updated);
      setStorageKeyData(STORAGE_KEY, {
        key: 'ReadNotificationIds',
        value: readIds
      });

      return updated;
    });

    // In real implementation, you would also:
    // - Send to backend: markAllNotificationsAsRead(allNotificationIds);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <>
      <IconButton
        size="small"
        color="inherit"
        onClick={handleClick}
        aria-label="notifications"
        sx={{
          width: theme.spacing(4.5),
          height: theme.spacing(4.5),
          alignSelf: 'center'
        }}
      >
        <Badge
          variant="dot"
          invisible={!hasUnreadNotifications}
          color="info"
          overlap="circular"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <NotificationsOutlinedIcon sx={{ fontSize: theme.spacing(3) }} />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <NRNotificationsInAppContainer
          onNotificationClick={handleNotificationClick}
          onNotificationsLoaded={setNotifications}
          onMarkAllAsRead={handleMarkAllAsRead}
          onViewAllNotifications={handleClose}
        />
      </Popover>
    </>
  );
}

export default NRNotificationsInApp;
